/**
 * ActivityFeedBlockController — bloc « Activité récente » du dashboard
 * payant (EPIC-029 / 29.4 — §15.C).
 *
 * Charge les 10 derniers événements via GET /activity-feed au connect()
 * (pas de polling) et rend la liste via le <template> data-target="itemTemplate"
 * — aucun HTML inline JS. Verbatim maquette `tableau-de-bord.html` (sélecteurs
 * `nm-timeline__when` / `__what` / `__who`, lien pied vers `/activite`).
 *
 * Mapping `eventType` + `metadata` → libellé / href partagé avec la page
 * dédiée 29.5 via `utils/activity_feed_mapping.js`. Pour un `eventType`
 * non reconnu, le libellé brut est rendu, sans href (lien neutralisé).
 *
 * États visibles : `loading` (au connect), `list` (≥1 événement), `empty`
 * (0 événement OU erreur réseau hors 401 — message neutre, jamais d'erreur
 * technique côté UI). 401 reste géré globalement par `api.js`.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";
import { buildEventMapping } from "/js/utils/activity_feed_mapping.js";

export default class ActivityFeedBlockController extends BaseController {
    static targets = ["loading", "list", "empty", "itemTemplate"];

    connect() {
        this.load();
    }

    async load() {
        this.showLoading();
        try {
            const response = await api.activityFeed.list({
                perPage: 10,
                sort: "-occurredAt",
            });
            const events = Array.isArray(response?.data) ? response.data : [];
            if (events.length === 0) {
                this.showEmpty();

                return;
            }
            this.renderItems(events);
        } catch {
            // 401 déjà géré globalement par api.js (redirect /connexion.html).
            // Toute autre erreur → état vide neutre (pas de message
            // technique côté UI utilisateur).
            this.showEmpty();
        }
    }

    renderItems(events) {
        this.listTarget.replaceChildren();
        for (const event of events) {
            const fragment = this.itemTemplateTarget.content.cloneNode(true);
            this.fillEvent(fragment, event);
            this.listTarget.appendChild(fragment);
        }
        this.loadingTarget.hidden = true;
        this.emptyTarget.hidden = true;
        this.listTarget.hidden = false;
    }

    fillEvent(fragment, event) {
        const whenEl = fragment.querySelector('[data-slot="when"]');
        const whatEl = fragment.querySelector('[data-slot="what"]');
        const whoEl = fragment.querySelector('[data-slot="who"]');
        const linkEl = fragment.querySelector('[data-slot="link"]');

        const occurredAt = event?.occurredAt ?? "";
        if (whenEl) {
            whenEl.textContent = this.formatTimeAgo(occurredAt);
            if (occurredAt) {
                whenEl.setAttribute("title", occurredAt);
            }
        }

        const mapping = buildEventMapping(event);
        if (whatEl) whatEl.textContent = mapping.label;
        if (whoEl) whoEl.textContent = mapping.domainLabel;
        if (linkEl) {
            if (mapping.href) {
                linkEl.setAttribute("href", mapping.href);
            } else {
                linkEl.removeAttribute("href");
            }
        }
    }

    showLoading() {
        this.loadingTarget.hidden = false;
        this.listTarget.hidden = true;
        this.emptyTarget.hidden = true;
    }

    showEmpty() {
        this.loadingTarget.hidden = true;
        this.listTarget.hidden = true;
        this.emptyTarget.hidden = false;
    }
}
